import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnTree from "@shared/components/GridV2/SnTree";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "@widgets/CommonDialog";
import { dateToString } from "@shared/utils/DateUtils";
import { SnButton } from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string;
  [key: string]: any;
}

const SN_MAS_BAS_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
  });

  const [columns, setColums] = useState<any>({
    tree: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    tree: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setColums({
          tree: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const onSearch = async () => {
    const modifiedRows = gridRefs.current["tree"].current.getChange();
    if (modifiedRows.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const result: any = await onSave();
        if (!result) return;
      }
    }

    try {
      const params = {
        id: "sn_mas_bas_reg_003_01",
        params: { cd_firm: userInfo.cd_firm },
      };

      gridRefs.current["tree"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        tree: data[0],
      });
    } catch (error) {}
  };

  const onInsert = async () => {
    try {
      const selectedNodes = gridRefs.current["tree"].current.getSelectedNodes();
      const selectedNode =
        selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null;

      // 신규 행 기본 데이터
      const newRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_user_reg: userInfo.cd_user,
        yn_end: "Y",
        yn_use: "Y",
      };

      if (!selectedNode) {
        // parent 없음 → 루트에 추가
        newRow.cd_group_p = null;
        newRow.nm_group_p = null;
        newRow.no_seq_sort = 1;
        newRow.no_level = 1;
        gridRefs.current["tree"].current?.addRow(newRow, null);
        return;
      }

      // 선택된 노드가 있는 경우
      const parentNode = selectedNode.parent;

      // 같은 레벨의 형제 노드들
      const siblings = parentNode
        ? parentNode.childrenAfterGroup || []
        : gridRefs.current["tree"].current?.getDisplayedRowAtIndex(0)?.parent
            ?.childrenAfterGroup || [];

      // 형제들 중 no_seq_sort 최대값 계산
      const maxNoLine =
        siblings.length > 0
          ? Math.max(
              ...siblings.map((child: any) => child.data.no_seq_sort || 0)
            )
          : 0;

      newRow.cd_group_p = parentNode.data ? parentNode.data.cd_group : null;
      newRow.no_seq_sort = maxNoLine + 1;
      newRow.no_level = selectedNode.data?.no_level;

      gridRefs.current["tree"].current?.addRow(newRow, parentNode);
    } catch (error: any) {}
  };

  const onDelete = () => {
    gridRefs.current["tree"].current?.deleteSelectedRows();
  };

  const onSave = async () => {
    try {
      gridRefs.current["tree"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["tree"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const changes = gridRefs.current["tree"].current.getChange();
      if (changes.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const insertList = changes.insert;
      for (const row of insertList) {
        const cd_group = row.cd_group;
        if (cd_group?.includes("temp-")) {
          await showDialog(
            "alter",
            "temp 품목군이 존재합니다. 품목군을 입력해주세요."
          );
          return false;
        }
      }

      const saveData = {
        mas_itemgroup: changes,
      };

      const ok = await fnSave("SN_MAS_BAS_REG_003_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["tree"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["tree"].current.resetUpdate(true);
      return false;
    }
  };

  const autoGroupColumnDef = {
    headerName: "품목군",
    field: "nm_group",
    width: 300,
    cellRendererParams: {
      suppressCount: true, // 하위 아이템 개수 숨기기
      suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
      suppressHoverHighlight: false,
    },
    rowDrag: true,
  };

  const getRowId = useCallback(
    (params: any) => String(params.data.cd_group),
    []
  );

  const onGrid1BtnsClick = async (id: string) => {
    const api = gridRefs.current["tree"].current;
    if (!api) return;

    if (id === "btnOpen") {
      api.setExpandedAll(true);
    }
    if (id === "btnClose") {
      api.setExpandedAll(false);
    }
    if (id === "btnAddRow") {
      const selectedNodes = api.getSelectedNodes();
      const selectedNode =
        selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null;

      if (!selectedNode) {
        await showDialog("alter", "상위 품목군을 선택해주세요.");
        return;
      }

      if (
        !selectedNode.data?.cd_group ||
        String(selectedNode.data.cd_group).startsWith("temp-")
      ) {
        await showDialog("alter", "품목군을 입력해주세요.");
        return;
      }

      const selectedNodeChildren = selectedNode.childrenAfterGroup || [];
      const maxNoLine =
        selectedNodeChildren.length > 0
          ? Math.max(
              ...selectedNodeChildren.map(
                (child: any) => child.data.no_seq_sort || 0
              )
            )
          : 0;

      // 닫혀 있으면 열기
      if (!selectedNode.expanded) {
        selectedNode.setExpanded(true);
        api.onGroupExpandedOrCollapsed();
      }

      // 신규행 구성
      const newRow: RowData = {
        cd_firm: userInfo.cd_firm,
        cd_user_reg: userInfo.cd_user,
        cd_group_p: selectedNode.data.cd_group,
        nm_group_p: selectedNode.data.nm_group,
        no_seq_sort: maxNoLine + 1,
        no_level: selectedNode.data?.no_level + 1,
        yn_end: "Y",
        yn_use: "Y",
        dt_start: dateToString(new Date()),
      };
      api.addRow(newRow, selectedNode);
    }
  };

  const onRowDataReady = () => {
    gridRefs.current["tree"].current.selectAndFocusRow(0);
  };

  const grids = useMemo(
    () => ({
      tree: {
        columns: columns.tree.map((col: any) => {
          if (col.cd_column === "cd_group") {
            return {
              ...col,

              valueFormatter: (params: any) => {
                const value = params.value;

                if (typeof value === "string" && value.startsWith("temp-")) {
                  return "";
                }

                return value;
              },
            };
          }

          return col;
        }),
        dataSource: dataSources.tree,
        events: {
          onRowDataReady: onRowDataReady,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div style={{ flex: "1 1 auto", display: "flex", gap: "10px" }}>
      <div style={{ flex: "1 1 100%", overflow: "auto" }}>
        <SnTree
          ref={gridRefs.current["tree"]}
          columnConfig={grids.tree.columns}
          rowData={grids.tree.dataSource}
          etcProps={{
            isEditable: true,
            rowSelection: "multiple",
            title: "상세정보",
          }}
          header={
            <div>
              <SnButton
                text="전체전개"
                onClick={() => onGrid1BtnsClick("btnOpen")}
              />
              <SnButton
                text="전체닫기"
                onClick={() => onGrid1BtnsClick("btnClose")}
              />
              <SnButton
                text="하위레벨추가"
                onClick={() => onGrid1BtnsClick("btnAddRow")}
              />
            </div>
          }
          events={grids.tree.events}
          treeProps={{
            treeDataParentIdField: "cd_group_p",
            treeIdKey: "cd_group",
            treeOrderKey: "no_seq_sort",
            groupDefaultExpanded: -1,
            autoGroupColumnDef: autoGroupColumnDef,
            getRowId: getRowId,
          }}
        />
      </div>
    </div>
  );
});

export default SN_MAS_BAS_REG_003;
